import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { logError } from '../utils/log';

const gameStructure = [
    { name: '301 Singles', type: 'S301' }, { name: '301 Singles', type: 'S301' }, { name: '301 Doubles', type: 'D301' },
    { name: '501 Singles', type: 'S501' }, { name: '501 Singles', type: 'S501' }, { name: '501 Doubles', type: 'D501' },
    { name: 'Cricket Singles', type: 'SCricket' }, { name: 'Cricket Singles', type: 'SCricket' }, { name: 'Cricket Doubles', type: 'DCricket' },
];

const gameSortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'gamePoints', label: 'Game Points' },
    { value: 'matchWins', label: 'Match Wins' },
    { value: 'weeksPlayed', label: 'Weeks Played' },
    { value: 'S301.winPct', label: 'S301 Win %' },
    { value: 'D301.winPct', label: 'D301 Win %' },
    { value: 'S501.winPct', label: 'S501 Win %' },
    { value: 'D501.winPct', label: 'D501 Win %' },
    { value: 'SCricket.winPct', label: 'SCricket Win %' },
    { value: 'DCricket.winPct', label: 'DCricket Win %' },
];

const highlightSortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'totalHighlights', label: 'Total Highlights' },
    { value: 'totalTons', label: 'Total Tons' },
    { value: 'highestTon', label: 'Highest Ton' },
    { value: 'total180s', label: '180s' },
    { value: 'highestIn', label: 'High In' },
    { value: 'highestOut', label: 'High Out' },
    { value: 'totalC9', label: '9-Hits' },
    { value: 'totalC8', label: '8-Hits' },
    { value: 'totalC7', label: '7-Hits' },
    { value: 'totalC6', label: '6-Hits' },
];

const allSortOptions = [
    ...gameSortOptions,
    ...highlightSortOptions.filter(opt => opt.value !== 'name')
];

export const TeamStatsCardsPage = () => {
    const [teamStats, setTeamStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('game'); // 'game', 'highlight', 'all'
    const [sortConfig, setSortConfig] = useState({ key: 'gamePoints', direction: 'desc' });
    const [currentSchedule, setCurrentSchedule] = useState(null);
    const [allSchedules, setAllSchedules] = useState([]);
    const [selectedScheduleId, setSelectedScheduleId] = useState('');
    
    const [selectedTeamForHistory, setSelectedTeamForHistory] = useState(null);
    const [selectedTeamForHighlights, setSelectedTeamForHighlights] = useState(null);

    const currentSortOptions = useMemo(() => {
        if (view === 'game') return gameSortOptions;
        if (view === 'highlight') return highlightSortOptions;
        return allSortOptions;
    }, [view]);

    useEffect(() => {
        setSortConfig(prev => {
            if (!currentSortOptions.find(opt => opt.value === prev.key)) {
                return { key: currentSortOptions[1]?.value || 'name', direction: 'desc' };
            }
            return prev;
        });
    }, [currentSortOptions]);

    const renderSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return <span className="text-info ml-1 text-[10px]">{sortConfig.direction === 'desc' ? '▼' : '▲'}</span>;
        }
        return null;
    };

    useEffect(() => {
        const fetchSchedules = async () => {
            setLoading(true);
            try {
                const schedulesSnapshot = await getDocs(collection(db, "schedules"));
                const schedulesList = schedulesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                schedulesList.sort((a, b) => {
                    if (a.archived !== b.archived) return a.archived ? 1 : -1;
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });
                setAllSchedules(schedulesList);

                if (schedulesList.length > 0) {
                    const latestNonArchived = schedulesList.find(s => !s.archived);
                    setSelectedScheduleId(latestNonArchived ? latestNonArchived.id : schedulesList[0].id);
                } else {
                    setLoading(false);
                }
            } catch (error) {
                logError("Error fetching schedules: ", error);
                setLoading(false);
            }
        };
        fetchSchedules();
    }, []);

    useEffect(() => {
        if (!selectedScheduleId || allSchedules.length === 0) {
            if (allSchedules.length > 0) setLoading(false);
            return;
        }
        const calculateTeamStats = async () => {
            setLoading(true);
            try {
                const scheduleForStats = allSchedules.find(s => s.id === selectedScheduleId);
                setCurrentSchedule(scheduleForStats);

                if (!scheduleForStats) {
                    setTeamStats([]);
                    setLoading(false);
                    return;
                }

                const teamsSnapshot = await getDocs(collection(db, 'teams'));
                const allTeamsData = teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                const participatingTeamIds = new Set();
                (scheduleForStats.weeks || []).forEach(week => {
                    (week.games || []).forEach(match => {
                        if (match.homeTeamId && match.homeTeamId !== 'TBD') participatingTeamIds.add(match.homeTeamId);
                        if (match.awayTeamId && match.awayTeamId !== 'TBD') participatingTeamIds.add(match.awayTeamId);
                    });
                });
                const teamsData = allTeamsData.filter(t => participatingTeamIds.has(t.id));

                const statsData = {};
                teamsData.forEach(team => {
                    statsData[team.id] = {
                        id: team.id,
                        name: team.name,
                        gamePoints: 0, matchWins: 0, matchLosses: 0,
                        weeksPlayed: new Set(),
                        S301: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        D301: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        S501: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        D501: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        SCricket: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        DCricket: { w: 0, l: 0, legsWon: 0, legsPlayed: 0, winPct: 0 },
                        gameHistory: [],
                        matchHistory: [],
                        highlightHistory: [],
                        totalHighlights: 0, totalTons: 0, highestTon: 0, total180s: 0,
                        totalHighIns: 0, highestIn: 0, totalHighOuts: 0, highestOut: 0,
                        totalC6: 0, totalC7: 0, totalC8: 0, totalC9: 0,
                    };
                });

                if (scheduleForStats) {
                    (scheduleForStats.weeks || []).forEach(week => {
                        const weekIdentifier = `${scheduleForStats.id}-${week.week}`;
                        (week.games || []).forEach(match => {
                            if (!match.results) return;
                            const homeTeamId = match.homeTeamId;
                            const awayTeamId = match.awayTeamId;

                            const homeEntry = statsData[homeTeamId];
                            const awayEntry = statsData[awayTeamId];

                            // Helper to capture a record before updating it for modal logic
                            const getMatchSnapshot = (tid) => {
                                if (!statsData[tid]) return null;
                                const s = statsData[tid];
                                const totalMatches = s.matchWins + s.matchLosses;
                                return { w: s.matchWins, l: s.matchLosses, winPct: totalMatches > 0 ? s.matchWins / totalMatches : 0 };
                            };
                            
                            const getGameSnapshot = (tid, type) => {
                                if (!statsData[tid]) return null;
                                const s = statsData[tid][type];
                                return { w: s.w, l: s.l, winPct: s.legsPlayed > 0 ? s.legsWon / s.legsPlayed : 0 };
                            };

                            const homeMatchRecordBefore = getMatchSnapshot(homeTeamId);
                            const awayMatchRecordBefore = getMatchSnapshot(awayTeamId);

                            let homeMatchGamePoints = 0;
                            let awayMatchGamePoints = 0;

                            (match.results.gameScores || []).forEach((score, idx) => {
                                const gameType = gameStructure[idx]?.type;
                                if (!gameType) return;
                                const gameWonByHome = (score.home || 0) > (score.away || 0);

                                const homeGameRecordBefore = getGameSnapshot(homeTeamId, gameType);
                                const awayGameRecordBefore = getGameSnapshot(awayTeamId, gameType);

                                if (homeEntry) {
                                    homeEntry[gameType].legsWon += (score.home || 0);
                                    homeEntry[gameType].legsPlayed += (score.home || 0) + (score.away || 0);
                                    if (gameWonByHome) homeEntry[gameType].w++; else homeEntry[gameType].l++;

                                    homeEntry.gameHistory.push({
                                        week: week.week, date: week.date,
                                        gameName: gameStructure[idx].name, gameType,
                                        teamLegs: score.home, opponentLegs: score.away,
                                        won: gameWonByHome, opponentId: awayTeamId,
                                        teamRecordAtTime: homeGameRecordBefore || { w: 0, l: 0, winPct: 0 },
                                        opponentRecordAtTime: awayGameRecordBefore || { w: 0, l: 0, winPct: 0 }
                                    });
                                }
                                if (awayEntry) {
                                    awayEntry[gameType].legsWon += (score.away || 0);
                                    awayEntry[gameType].legsPlayed += (score.home || 0) + (score.away || 0);
                                    if (!gameWonByHome) awayEntry[gameType].w++; else awayEntry[gameType].l++;

                                    awayEntry.gameHistory.push({
                                        week: week.week, date: week.date,
                                        gameName: gameStructure[idx].name, gameType,
                                        teamLegs: score.away, opponentLegs: score.home,
                                        won: !gameWonByHome, opponentId: homeTeamId,
                                        teamRecordAtTime: awayGameRecordBefore || { w: 0, l: 0, winPct: 0 },
                                        opponentRecordAtTime: homeGameRecordBefore || { w: 0, l: 0, winPct: 0 }
                                    });
                                }

                                if (gameWonByHome) homeMatchGamePoints++; else awayMatchGamePoints++;
                            });

                            if (homeEntry) {
                                homeEntry.weeksPlayed.add(weekIdentifier);
                                homeEntry.gamePoints += homeMatchGamePoints;
                                if (homeMatchGamePoints > awayMatchGamePoints) homeEntry.matchWins++; else if (homeMatchGamePoints < awayMatchGamePoints) homeEntry.matchLosses++;
                                
                                homeEntry.matchHistory.push({
                                    week: week.week, date: week.date,
                                    teamPoints: homeMatchGamePoints, opponentPoints: awayMatchGamePoints,
                                    won: homeMatchGamePoints > awayMatchGamePoints, opponentId: awayTeamId,
                                    teamRecordAtTime: homeMatchRecordBefore || { w: 0, l: 0, winPct: 0 },
                                    opponentRecordAtTime: awayMatchRecordBefore || { w: 0, l: 0, winPct: 0 }
                                });
                            }
                            if (awayEntry) {
                                awayEntry.weeksPlayed.add(weekIdentifier);
                                awayEntry.gamePoints += awayMatchGamePoints;
                                if (awayMatchGamePoints > homeMatchGamePoints) awayEntry.matchWins++; else if (awayMatchGamePoints < homeMatchGamePoints) awayEntry.matchLosses++;

                                awayEntry.matchHistory.push({
                                    week: week.week, date: week.date,
                                    teamPoints: awayMatchGamePoints, opponentPoints: homeMatchGamePoints,
                                    won: awayMatchGamePoints > homeMatchGamePoints, opponentId: homeTeamId,
                                    teamRecordAtTime: awayMatchRecordBefore || { w: 0, l: 0, winPct: 0 },
                                    opponentRecordAtTime: homeMatchRecordBefore || { w: 0, l: 0, winPct: 0 }
                                });
                            }

                            // Highlight Processing (Grouped by team for privacy)
                            const assignmentMap = {};
                            Object.keys(match.results.playerAssignments || {}).forEach(key => {
                                const playerId = match.results.playerAssignments[key];
                                const isHome = key.includes('home');
                                assignmentMap[playerId] = isHome ? homeTeamId : awayTeamId;
                            });

                            const weekHighlights = {
                                [homeTeamId]: { tons: [], '180s': 0, highIns: [], highOuts: [], c9: 0, c8: 0, c7: 0, c6: 0, total: 0 },
                                [awayTeamId]: { tons: [], '180s': 0, highIns: [], highOuts: [], c9: 0, c8: 0, c7: 0, c6: 0, total: 0 }
                            };

                            const processedPlayers = new Set();
                            const highlightsByPlayer = match.results.highlights || {};
                            
                            Object.keys(highlightsByPlayer).forEach(playerId => {
                                if (processedPlayers.has(playerId)) return;
                                const teamId = assignmentMap[playerId];
                                const entry = statsData[teamId];
                                if (!entry) return;
                                
                                const h = highlightsByPlayer[playerId] || {};
                                const wh = weekHighlights[teamId];
                                let delta = 0;

                                if (h.tons) {
                                    const tons = h.tons.split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
                                    entry.totalTons += tons.length; delta += tons.length;
                                    wh.tons.push(...tons);
                                    const maxTon = Math.max(0, ...tons);
                                    if (maxTon > entry.highestTon) entry.highestTon = maxTon;
                                }
                                if (h['180s']) {
                                    const c = parseInt(h['180s'], 10);
                                    if (!isNaN(c)) { entry.total180s += c; delta += c; wh['180s'] += c; }
                                }
                                if (h.highIn || h.on80) {
                                    const ins = (h.highIn || h.on80).split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
                                    entry.totalHighIns += ins.length; delta += ins.length;
                                    wh.highIns.push(...ins);
                                    const maxIn = Math.max(0, ...ins);
                                    if (maxIn > entry.highestIn) entry.highestIn = maxIn;
                                }
                                if (h.highOut) {
                                    const outs = h.highOut.split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
                                    entry.totalHighOuts += outs.length; delta += outs.length;
                                    wh.highOuts.push(...outs);
                                    const maxOut = Math.max(0, ...outs);
                                    if (maxOut > entry.highestOut) entry.highestOut = maxOut;
                                }
                                ['c9','C9','c8','C8','c7','C7','c6','C6'].forEach(key => {
                                    const val = parseInt(h[key] ?? '0', 10);
                                    if (!isNaN(val) && val > 0) {
                                        const lk = key.toLowerCase();
                                        if (lk === 'c9') { entry.totalC9 += val; wh.c9 += val; }
                                        else if (lk === 'c8') { entry.totalC8 += val; wh.c8 += val; }
                                        else if (lk === 'c7') { entry.totalC7 += val; wh.c7 += val; }
                                        else if (lk === 'c6') { entry.totalC6 += val; wh.c6 += val; }
                                        delta += val;
                                    }
                                });

                                entry.totalHighlights += delta;
                                wh.total += delta;
                                processedPlayers.add(playerId);
                            });

                            if (weekHighlights[homeTeamId].total > 0 && homeEntry) {
                                homeEntry.highlightHistory.push({ week: week.week, date: week.date, ...weekHighlights[homeTeamId] });
                            }
                            if (weekHighlights[awayTeamId].total > 0 && awayEntry) {
                                awayEntry.highlightHistory.push({ week: week.week, date: week.date, ...weekHighlights[awayTeamId] });
                            }
                        });
                    });
                }

                const statsArray = Object.values(statsData);
                statsArray.forEach(team => {
                    team.weeksPlayed = team.weeksPlayed.size;
                    ['S301','D301','S501','D501','SCricket','DCricket'].forEach(key => {
                        const totalLegs = team[key].legsPlayed;
                        team[key].winPct = totalLegs > 0 ? (team[key].legsWon / totalLegs) : 0;
                    });
                });

                setTeamStats(statsArray);
            } catch (error) {
                logError('Error calculating team stats: ', error);
            }
            setLoading(false);
        };
        calculateTeamStats();
    }, [selectedScheduleId, allSchedules]);

    const sortedTeamStats = useMemo(() => {
        let sortableItems = [...teamStats];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                const keys = sortConfig.key.split('.');
                const aValue = keys.reduce((obj, key) => obj && obj[key], a);
                const bValue = keys.reduce((obj, key) => obj && obj[key], b);

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }

                // If primary sort key is gamePoints and they are tied, use head-to-head as a tie-breaker.
                if (sortConfig.key === 'gamePoints') {
                    if (currentSchedule && currentSchedule.weeks) {
                        for (const week of currentSchedule.weeks) {
                            for (const game of week.games) {
                                const isMatch = (game.homeTeamId === a.id && game.awayTeamId === b.id) || (game.homeTeamId === b.id && game.awayTeamId === a.id);
                                if (isMatch && game.results && game.results.matchStats) {
                                    const homePoints = game.results.matchStats.home.gamePoints;
                                    const awayPoints = game.results.matchStats.away.gamePoints;
                                    if (homePoints !== awayPoints) {
                                        const winnerId = homePoints > awayPoints ? game.homeTeamId : game.awayTeamId;
                                        if (winnerId === a.id) return -1; // a wins
                                        if (winnerId === b.id) return 1;  // b wins
                                    }
                                }
                            }
                        }
                    }
                }
                return a.name.localeCompare(b.name); // Final alphabetical sort for stability
            });
        }
        return sortableItems;
    }, [teamStats, sortConfig, currentSchedule]);

    if (loading) {
        return <div className="text-center">Loading team stats...</div>;
    }

    return (
        <div className="space-y-6">
            <h2 className="page-title heading-primary-1 text-4xl font-extrabold text-center mb-6">Team Statistics</h2>
            
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
                <div className="flex gap-2 items-center w-full sm:w-auto">
                    <label htmlFor="season-select" className="text-sm font-semibold text-muted whitespace-nowrap">Season:</label>
                    <select
                        id="season-select"
                        value={selectedScheduleId}
                        onChange={e => setSelectedScheduleId(e.target.value)}
                        className="select-field p-2 w-full sm:w-auto"
                        disabled={loading || allSchedules.length === 0}
                    >
                        {allSchedules.map(s => (
                            <option key={s.id} value={s.id}>
                                {s.name} {s.archived ? '(Archived)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <button onClick={() => setView('game')} className={`flex-1 sm:flex-none ${view === 'game' ? 'segmented-btn-active' : 'segmented-btn'}`}>Game</button>
                    <button onClick={() => setView('highlight')} className={`flex-1 sm:flex-none ${view === 'highlight' ? 'segmented-btn-active' : 'segmented-btn'}`}>Highlight</button>
                    <button onClick={() => setView('all')} className={`flex-1 sm:flex-none ${view === 'all' ? 'segmented-btn-active' : 'segmented-btn'}`}>All</button>
                </div>
                
                <div className="flex gap-2 items-center w-full sm:w-auto">
                    <label className="text-sm font-semibold text-muted whitespace-nowrap">Sort By:</label>
                    <select 
                        value={sortConfig.key} 
                        onChange={(e) => setSortConfig({ ...sortConfig, key: e.target.value })} 
                        className="select-field p-2 w-full sm:w-auto"
                    >
                        {currentSortOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    <button 
                        onClick={() => setSortConfig({ ...sortConfig, direction: sortConfig.direction === 'asc' ? 'desc' : 'asc' })} 
                        className="btn btn-secondary px-3"
                        title="Toggle sort direction"
                    >
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </button>
                </div>
            </div>

            {currentSchedule?.archived && (<p className="text-center text-sm text-warning -mt-4">You are viewing stats for an archived season.</p>)}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {sortedTeamStats.map((team, index) => (
                    <div key={team.id} className="card rounded-lg shadow-md p-4 sm:p-5 flex flex-col gap-4">
                        <div className="flex justify-between items-center border-b border-low pb-3">
                            <div className="flex items-center gap-2 overflow-hidden mr-2">
                                <span className="tag tag-primary-one shrink-0 shadow-sm" title="Rank">#{index + 1}</span>
                                <h3 className="text-xl font-bold heading-primary-1 truncate flex items-center" title={team.name}>{team.name}{renderSortIndicator('name')}</h3>
                            </div>
                            <span className="tag tag-primary-one shrink-0 flex items-center">WP: {team.weeksPlayed}{renderSortIndicator('weeksPlayed')}</span>
                        </div>
                        
                        {(view === 'game' || view === 'all') && (
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="flex-1 surface-low p-3 rounded-lg border border-low">
                                    <h4 className="font-semibold text-muted text-xs uppercase tracking-wider mb-2">Match & Game Points</h4>
                                    <div className="space-y-1 text-sm">
                                        <div
                                            className="flex justify-between p-1 -mx-1 rounded hover:surface-low cursor-pointer transition-colors"
                                            onClick={() => setSelectedTeamForHistory({ team, filter: 'all', display: 'matches' })}
                                        >
                                            <span className="text-muted flex items-center">Match (W-L){renderSortIndicator('matchWins')}</span> 
                                            <span className="font-bold text-strong">{team.matchWins}-{team.matchLosses}</span>
                                        </div>
                                        <div
                                            className="flex justify-between p-1 -mx-1 rounded hover:surface-low cursor-pointer transition-colors"
                                            onClick={() => setSelectedTeamForHistory({ team, filter: 'all', display: 'games' })}
                                        >
                                            <span className="text-muted flex items-center">Game Points{renderSortIndicator('gamePoints')}</span> 
                                            <span className="font-bold text-info">{team.gamePoints}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-[2] surface-low p-3 rounded-lg border border-low">
                                    <h4 className="font-semibold text-muted text-xs uppercase tracking-wider mb-2">Record (Leg Win%)</h4>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 text-xs sm:text-sm">
                                        {['S301', 'D301', 'S501', 'D501', 'SCricket', 'DCricket'].map(type => (
                                            <div
                                                key={type}
                                                className="flex flex-col p-1.5 rounded hover:surface-low cursor-pointer transition-colors" 
                                                onClick={() => setSelectedTeamForHistory({ team, filter: type, display: 'games' })}
                                            >
                                                <span className="font-semibold text-muted flex items-center">{type}{renderSortIndicator(`${type}.winPct`)}</span>
                                                <span className="font-bold text-strong">{team[type].w}-{team[type].l} <span className="text-muted font-normal text-xs ml-1">({team[type].winPct.toFixed(3)})</span></span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {(view === 'highlight' || view === 'all') && (
                            <div 
                                className="surface-low p-3 rounded-lg border border-low cursor-pointer hover:opacity-80 transition-opacity" 
                                onClick={() => setSelectedTeamForHighlights(team)}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold text-muted text-xs uppercase tracking-wider">Highlights</h4>
                                    <span className="tag tag-warning text-xs font-bold shrink-0 flex items-center">Total: {team.totalHighlights}{renderSortIndicator('totalHighlights')}</span>
                                </div>
                                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 text-xs text-center">
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">Tons{renderSortIndicator('totalTons')}</span><span className="font-bold text-strong">{team.totalTons}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">High Ton{renderSortIndicator('highestTon')}</span><span className="font-bold text-strong">{team.highestTon || '-'}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">180s{renderSortIndicator('total180s')}</span><span className="font-bold text-strong">{team.total180s}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">High In{renderSortIndicator('highestIn')}</span><span className="font-bold text-strong">{team.highestIn || '-'}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">High Out{renderSortIndicator('highestOut')}</span><span className="font-bold text-strong">{team.highestOut || '-'}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">9-Hits{renderSortIndicator('totalC9')}</span><span className="font-bold text-strong">{team.totalC9}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">8-Hits{renderSortIndicator('totalC8')}</span><span className="font-bold text-strong">{team.totalC8}</span></div>
                                    <div className="flex flex-col"><span className="text-muted flex justify-center items-center">7{renderSortIndicator('totalC7')}/6-Hits{renderSortIndicator('totalC6')}</span><span className="font-bold text-strong">{team.totalC7}/{team.totalC6}</span></div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {selectedTeamForHistory && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-overlay" onClick={() => setSelectedTeamForHistory(null)}>
                    <div className="card rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-3 sm:p-4 border-b border-low">
                            <h3 className="text-xl font-bold heading-primary-1">
                                {selectedTeamForHistory.team.name}
                            </h3>
                            <button onClick={() => setSelectedTeamForHistory(null)} className="text-muted hover:text-strong text-2xl leading-none">&times;</button>
                        </div>
                        <div className="p-2 sm:p-4 overflow-y-auto space-y-3">
                            {(() => {
                                const { team, filter, display } = selectedTeamForHistory;
                                const historyList = display === 'matches' 
                                    ? team.matchHistory 
                                    : team.gameHistory.filter(g => filter === 'all' || g.gameType === filter);

                                if (historyList.length === 0) {
                                    return <p className="text-center text-muted py-4">No match history available.</p>;
                                }
                                return historyList.map((item, i) => {
                                    const opponent = teamStats.find(t => t.id === item.opponentId);
                                    const oppName = opponent ? opponent.name : 'Unknown';
                                    
                                    let highlightClass = '';
                                    if (item.teamRecordAtTime && item.opponentRecordAtTime) {
                                        let compPct = item.opponentRecordAtTime.winPct;
                                        if (item.won && item.teamRecordAtTime.winPct < compPct) {
                                            highlightClass = 'bg-success-soft';
                                        } else if (!item.won && item.teamRecordAtTime.winPct > compPct) {
                                            highlightClass = 'bg-danger-soft';
                                        }
                                    }

                                    const gameTitle = display === 'matches' 
                                        ? 'Overall Match' 
                                        : (filter !== 'all' ? item.gameName.replace(' Singles', '').replace(' Doubles', '') : item.gameName);

                                    const recordTitle = display === 'matches' ? 'Match W-L' : 'Game W-L';

                                    return (
                                        <div key={i} className={`p-3 rounded-lg border border-low flex flex-col sm:flex-row justify-between gap-3 ${highlightClass || 'surface-low'}`}>
                                            <div className="flex flex-col justify-center">
                                                <span className="font-bold text-sm text-strong">Week {item.week} - {gameTitle}</span>
                                                <span className="text-xs text-muted">{item.date}</span>
                                            </div>
                                            <div className="flex items-center justify-between sm:justify-end gap-6 sm:w-2/3">
                                                <div className="flex flex-col text-left sm:text-right">
                                                    <span className="text-sm font-semibold text-strong">vs {oppName}</span>
                                                    <span className="text-xs text-muted">Opp {recordTitle}: {item.opponentRecordAtTime?.w || 0}-{item.opponentRecordAtTime?.l || 0}</span>
                                                    <span className="text-xs text-muted">Team {recordTitle}: {item.teamRecordAtTime?.w || 0}-{item.teamRecordAtTime?.l || 0}</span>
                                                </div>
                                                <div className="text-center shrink-0">
                                                    <span className={`text-2xl font-extrabold ${item.won ? 'text-success' : 'text-danger'}`}>
                                                        {item.won ? 'W' : 'L'}
                                                    </span>
                                                    <span className="text-sm font-bold ml-1 text-strong block sm:inline">
                                                        {display === 'matches' ? `(${item.teamPoints}-${item.opponentPoints})` : `(${item.teamLegs}-${item.opponentLegs})`}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                        <div className="p-3 sm:p-4 border-t border-low surface-low rounded-b-lg">
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-[10px] sm:text-xs text-muted justify-center sm:justify-start">
                                <div className="flex items-center">
                                    <span className="w-3 h-3 inline-block bg-success-soft mr-1.5 rounded-sm"></span> Won vs better record
                                </div>
                                <div className="flex items-center">
                                    <span className="w-3 h-3 inline-block bg-danger-soft mr-1.5 rounded-sm"></span> Lost vs worse record
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedTeamForHighlights && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-overlay" onClick={() => setSelectedTeamForHighlights(null)}>
                    <div className="card rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-3 sm:p-4 border-b border-low">
                            <h3 className="text-xl font-bold heading-primary-1">
                                {selectedTeamForHighlights.name} - Highlights
                            </h3>
                            <button onClick={() => setSelectedTeamForHighlights(null)} className="text-muted hover:text-strong text-2xl leading-none">&times;</button>
                        </div>
                        <div className="p-2 sm:p-4 overflow-y-auto space-y-3">
                            {selectedTeamForHighlights.highlightHistory && selectedTeamForHighlights.highlightHistory.length > 0 ? (
                                selectedTeamForHighlights.highlightHistory.map((h, i) => (
                                    <div key={i} className="p-3 surface-low rounded-lg border border-low">
                                        <div className="flex justify-between items-center mb-3 border-b border-low pb-2">
                                            <span className="font-bold text-strong">Week {h.week}</span>
                                            <span className="tag tag-info text-xs font-bold">Total: {h.total}</span>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                            {h.tons && h.tons.length > 0 && <div><span className="text-muted text-xs block uppercase tracking-wider">Tons</span><span className="font-semibold text-strong">{h.tons.join(', ')}</span></div>}
                                            {h['180s'] > 0 && <div><span className="text-muted text-xs block uppercase tracking-wider">180s</span><span className="font-semibold text-strong">{h['180s']}</span></div>}
                                            {h.highIns && h.highIns.length > 0 && <div><span className="text-muted text-xs block uppercase tracking-wider">High In</span><span className="font-semibold text-strong">{h.highIns.join(', ')}</span></div>}
                                            {h.highOuts && h.highOuts.length > 0 && <div><span className="text-muted text-xs block uppercase tracking-wider">High Out</span><span className="font-semibold text-strong">{h.highOuts.join(', ')}</span></div>}
                                            {h.c9 > 0 && <div><span className="text-muted text-xs block uppercase tracking-wider">9-Hits</span><span className="font-semibold text-strong">{h.c9}</span></div>}
                                            {h.c8 > 0 && <div><span className="text-muted text-xs block uppercase tracking-wider">8-Hits</span><span className="font-semibold text-strong">{h.c8}</span></div>}
                                            {h.c7 > 0 && <div><span className="text-muted text-xs block uppercase tracking-wider">7-Hits</span><span className="font-semibold text-strong">{h.c7}</span></div>}
                                            {h.c6 > 0 && <div><span className="text-muted text-xs block uppercase tracking-wider">6-Hits</span><span className="font-semibold text-strong">{h.c6}</span></div>}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-muted py-4">No highlights recorded.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};